"use client";

import { getSupabaseBrowserClient } from "@pex/auth/client";
import { useEffect, useRef, useState } from "react";

interface UsePwaAuthOptions {
  /** Called when a Supabase session is detected after the popup flow completes. */
  onAuth?: () => void;
}

/**
 * Manages a popup-based auth flow for PWA contexts.
 *
 * On iOS Safari and desktop Chrome, a full-page cross-origin redirect from a
 * PWA abandons the shell. `window.open()` instead spawns an in-app browser
 * (SFSafariViewController / Custom Tab) that keeps the PWA alive.
 *
 * Usage:
 *   const { isPwa, openAuthPopup, isPolling } = usePwaAuth(accountsUrl, {
 *     onAuth: () => router.replace(returnTo),
 *   });
 */
export function usePwaAuth(accountsUrl: string, options?: UsePwaAuthOptions) {
  const [isPwa, setIsPwa] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Detect PWA mode after mount (matchMedia is not available server-side)
  useEffect(() => {
    setIsPwa(window.matchMedia("(display-mode: standalone)").matches);
  }, []);

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  };

  const startPolling = () => {
    setIsPolling(true);
    const supabase = getSupabaseBrowserClient();

    intervalRef.current = setInterval(async () => {
      // Check for a Supabase session — the shared cross-subdomain cookie may
      // have been set by the popup (Google OAuth) or by the magic link callback
      // tab, both of which write to the same cookie domain.
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        stopPolling();
        popupRef.current?.close();
        options?.onAuth?.();
        return;
      }

      // Popup was closed without completing auth — stop polling
      if (popupRef.current?.closed) {
        stopPolling();
      }
    }, 1000);
  };

  const openAuthPopup = () => {
    // If a popup is already open, bring it to focus
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }

    const loginUrl = new URL("/login", accountsUrl);
    loginUrl.searchParams.set("popup", "true");

    const popup = window.open(
      loginUrl.toString(),
      "pex-auth",
      "popup,width=500,height=640"
    );

    if (popup) {
      popupRef.current = popup;
      startPolling();
    }
  };

  // Clean up the polling interval when the component unmounts
  useEffect(() => {
    return () => stopPolling();
  }, []);

  return { isPwa, openAuthPopup, isPolling };
}
